import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel"
import { useBreakpoint } from "@/providers/BreakpointProvider";
import { cn } from "@/lib/utils/general";
import { ChevronDown, Paintbrush } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Theme } from "@/types/themes";
import { Button } from "@/components/ui/button";
import { THEME_ADJECTIVES } from "@/lib/utils/constants/adjectives";
import pixelArtBg from "@/assets/textures/pixel_art.png";
import neonPunkBg from "@/assets/textures/neon_punk.png";
import watercolorBg from "@/assets/textures/watercolor.png";

interface ThemeSelectorProps {
  themes: Theme[];
  onSelect: (theme: Theme) => void;
  isLoading?: boolean;
}

const artStyles = [
  { 
    id: 1, 
    name: "Pixel Art", 
    description: "Retro-inspired pixel graphics",
    background: pixelArtBg,
    blendMode: "multiply",
    blur: false
  },
  { 
    id: 2, 
    name: "Watercolor", 
    description: "Soft, dreamy watercolor style",
    background: watercolorBg,
    blendMode: "multiply",
    blur: "sm"
  },
  { 
    id: 3, 
    name: "Neon Punk", 
    description: "Bold, vibrant cyberpunk aesthetic",
    background: neonPunkBg,
    blendMode: "multiply",
    blur: "md"
  },
];

// Add this at the top level of the file, after imports and before the component
const pulseAnimation = `
@keyframes softPulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.025);
  }
  100% {
    transform: scale(1);
  }
}
`;

const ThemeSelector = ({ themes, onSelect, isLoading = false }: ThemeSelectorProps) => {
  const { isMobile } = useBreakpoint();
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [currentAdjective, setCurrentAdjective] = useState("");
  const [currentThemeNumber, setCurrentThemeNumber] = useState("1");
  const [currentArtStyleIndex, setCurrentArtStyleIndex] = useState(0);

  const getRandomAdjective = () => {
    return THEME_ADJECTIVES[Math.floor(Math.random() * THEME_ADJECTIVES.length)];
  };

  useEffect(() => {
    if (!api) return;

    api.on("select", () => {
      const currentIndex = api.selectedScrollSnap();
      setCurrent(currentIndex);
      setCurrentAdjective(getRandomAdjective());
      // Update current theme number
      if (themes[currentIndex]) {
        setCurrentThemeNumber(themes[currentIndex].themeNumber);
      }
    });
  }, [api, themes]);

  useEffect(() => {
    setCurrentAdjective(getRandomAdjective());
    // Set initial theme number
    if (themes[0]) {
      setCurrentThemeNumber(themes[0].themeNumber);
    }
  }, [themes]);

  const handleArtStyleClick = () => {
    setCurrentArtStyleIndex((prev) => (prev + 1) % artStyles.length);
  };

  const getThemeStyles = (themeNumber: string) => {
    const baseStyles = {
      overlay: "",
      icon: "",
      decoration: "",
      shadow: ""
    };

    switch (themeNumber) {
      case "1":
        return {
          ...baseStyles,
          overlay: "bg-red-500/60",
          icon: "text-red",
          decoration: "bg-red-400",
          shadow: "shadow-red-500/20"
        };
      case "2":
        return {
          ...baseStyles,
          overlay: "bg-emerald-500/60",
          icon: "text-emerald",
          decoration: "bg-emerald-400",
          shadow: "shadow-emerald-500/20"
        };
      case "3":
        return {
          ...baseStyles,
          overlay: "bg-indigo-500/70",
          icon: "text-indigo",
          decoration: "bg-indigo-800",
          shadow: "shadow-indigo-500/20"
        };
      default:
        return {
          ...baseStyles,
          overlay: "bg-muted/60",
          icon: "text-game-accent",
          decoration: "bg-game-accent",
          shadow: "shadow-white/10"
        };
    }
  };

  // Get current theme styles for the container shadow
  const currentStyles = getThemeStyles(currentThemeNumber);

  const renderLoadingItems = () => {
    return Array(3).fill(null).map((_, index) => (
      <CarouselItem 
        key={`skeleton-${index}`} 
        className={cn(
          isMobile ? "basis-[82%]" : "basis-full md:basis-1/2 lg:basis-1/3"
        )}
      >
        <div className={cn(
          "h-full",
          isMobile ? "aspect-[3/4]" : "aspect-[2/3]",
          "p-1"
        )}>
          <div className="flex flex-col items-center justify-center p-6 rounded-xl h-full bg-muted/50 animate-pulse">
            <div className="flex flex-col items-center gap-6 text-center w-full">
              <Skeleton className="h-12 w-3/4" />
              <Skeleton className="h-1 w-16 mt-auto" />
            </div>
          </div>
        </div>
      </CarouselItem>
    ));
  };

  return (
    <div className="w-full pb-8">
      <style>{pulseAnimation}</style>
      <Card 
        id="themeSelector" 
        className={cn(
          "w-full animate-slideUp bg-card shadow-lg",
          isLoading ? "shadow-white/10" : currentStyles.shadow
        )}
      >
        <CardContent id="themeSelectorContent" className={cn(
          "p-2",
          isMobile ? "px-0" : "p-6"
        )}>
          {/* Header */}
          <div className="text-center mb-6">
            <h2 className="text-md font-normal text-foreground mt-6 mb-2">
              {isLoading ? "Sowing Magic..." : `${currentAdjective} Game Ideas`}
            </h2>
            {isLoading && (
              <p className="text-sm text-muted-foreground">
                Crafting unique game concepts just for you
              </p>
            )}
          </div>

          <Carousel 
            setApi={setApi}
            opts={{ 
              align: "center",
              loop: true,
              slidesToScroll: 1,
              containScroll: "trimSnaps"
            }}
            className="w-full max-w-full"
            id="themeSelectorCarousel"
          >
            <CarouselContent id="themeSelectorCarouselContent">
              {isLoading ? renderLoadingItems() : (
                themes.length > 0 ? themes.map((theme) => {
                  const styles = getThemeStyles(theme.themeNumber);
                  return (
                    <CarouselItem 
                      key={theme.id} 
                      className={cn(
                        isMobile ? "basis-[82%]" : "basis-full md:basis-1/2 lg:basis-1/3"
                      )}
                    >
                      <div className={cn(
                        "h-full",
                        isMobile ? "aspect-[3/4]" : "aspect-[2/3]",
                        "p-1"
                      )}>
                        <div 
                          className={cn(
                            "relative flex flex-col items-center justify-center p-6 rounded-xl",
                            "h-full transition-all duration-200",
                            "active:animate-[softPulse_400ms_ease-in-out]",
                            "shadow-lg hover:shadow-xl",
                            "overflow-hidden"
                          )}
                          onClick={handleArtStyleClick}
                        >
                          {/* Background Image */}
                          <div 
                            className={cn(
                              "absolute inset-0 bg-cover bg-center bg-no-repeat",
                              {
                                "mix-blend-multiply": artStyles[currentArtStyleIndex].blendMode === "multiply",
                                "mix-blend-overlay": artStyles[currentArtStyleIndex].blendMode === "overlay",
                                "mix-blend-soft-light": artStyles[currentArtStyleIndex].blendMode === "soft-light",
                              },
                              {
                                "blur-none": !artStyles[currentArtStyleIndex].blur,
                                "blur-sm": artStyles[currentArtStyleIndex].blur === "sm",
                                "blur-md": artStyles[currentArtStyleIndex].blur === "md",
                                "blur-lg": artStyles[currentArtStyleIndex].blur === "lg",
                              }
                            )}
                            style={{ 
                              backgroundImage: `url(${artStyles[currentArtStyleIndex].background})`,
                            }}
                          />
                          {/* Color Overlay */}
                          <div className={cn(
                            "absolute inset-0",
                            styles.overlay
                          )} />
                          
                          {/* Content */}
                          <div className="relative flex flex-col items-center h-full z-10">
                            {/* Art Style Badge */}
                            <div className="mb-4 px-3 py-1 rounded-full bg-black/20 backdrop-blur-sm">
                              <span className="text-xs font-medium text-white/90 flex items-center gap-1">
                                <Paintbrush className="h-3 w-3" />
                                {artStyles[currentArtStyleIndex].name}
                              </span>
                            </div>
                            {/* Center title in the middle */}
                            <div className="flex-1 flex items-center justify-center">
                              <h3 className={cn(
                                "text-foreground font-extrabold leading-tight drop-shadow-xl font-instrument text-center",
                                isMobile ? 
                                  ((theme.title || theme.name).length > 10 && !(theme.title || theme.name).includes(' ')) 
                                    ? "text-2xl" 
                                    : "text-3xl"
                                  : "text-4xl"
                              )}>
                                {theme.title || theme.name}
                              </h3>
                            </div>
                            {/* Fixed height container for tagline */}
                            <div className="h-16 flex items-start justify-center mt-4">
                              <p className={cn(
                                "text-foreground/90 font-bold leading-snug text-center",
                                isMobile ? "text-sm" : "text-base",
                                "line-clamp-2"
                              )}>
                                {theme.tagline || "A new gaming adventure awaits..."}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CarouselItem>
                  );
                }) : (
                  <div className="text-center text-muted-foreground">
                    No themes available
                  </div>
                )
              )}
            </CarouselContent>

            {/* Navigation Controls */}
            <div className={cn(
              "mt-2 flex flex-col items-center justify-center gap-2",
              isMobile ? "flex" : "hidden"
            )}>
              {!isLoading && (
                <>
                  <span className="text-sm text-muted-foreground">
                    Theme {current + 1} of {themes.length}
                  </span>
                  <div className="flex flex-col items-center gap-2 text-xs text-muted-foreground mt-2">
                    <div className="flex items-center gap-1">
                      <ChevronDown className="h-4 w-4 animate-shake" />
                      <span>Swipe to see other game ideas</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Paintbrush className="h-3 w-3 animate-bounce" />
                      <span className="text-xs opacity-80">Tap to switch art styles</span>
                    </div>
                  </div>
                </>
              )}
            </div>
            
            <div className={cn(
              isMobile ? "hidden" : "block"
            )}>
              <CarouselPrevious className="bg-muted text-foreground hover:bg-muted/90 border-border" />
              <CarouselNext className="bg-muted text-foreground hover:bg-muted/90 border-border" />
            </div>
          </Carousel>
        </CardContent>
      </Card>

      {/* Continue Button */}
      {!isLoading && themes.length > 0 && (
        <div className="flex justify-center mt-6">
          <Button
            onClick={() => onSelect(themes[current])}
            className={cn(
              "px-8 py-2 rounded-full text-background font-medium transition-all",
              "bg-foreground",
              "hover:bg-foreground/90",
              "shadow-lg hover:shadow-xl"
            )}
          >
            Continue
          </Button>
        </div>
      )}
    </div>
  );
};

export { ThemeSelector }; 